import type Lenis from 'lenis'

// Smooth scroll + ScrollTrigger sync, scoped to opt-in pages only.
// A page enables it with `definePageMeta({ smoothScroll: true })`. Lenis is
// created when such a page is active and destroyed when leaving it, so the
// rest of the app (account, auth, ...) keeps native scrolling.
//
// Lenis, GSAP and ScrollTrigger are imported dynamically, only when a
// smooth-scroll page is actually active. A plugin always runs for the whole app
// and cannot be scoped to a layout, so static imports here put all three in the
// main bundle of every page — including the storefront and booking pages, which
// never scroll smoothly. The plugin itself has to stay: it also owns scroll
// position after every navigation, for every page (see `router.afterEach`).
export default defineNuxtPlugin(() => {
  const route = useRoute()
  let lenis: Lenis | null = null
  let teardown: (() => void) | null = null

  // Whether the current route wants Lenis. Checked again once the imports
  // resolve, so a navigation away during the import does not leave Lenis
  // running on a page that never asked for it.
  let wanted = false

  async function enable() {
    wanted = true
    if (lenis) return

    const [{ default: LenisClass }, { gsap }, { ScrollTrigger }] = await Promise.all([
      import('lenis'),
      import('gsap'),
      import('gsap/ScrollTrigger'),
    ])

    if (!wanted || lenis) return

    gsap.registerPlugin(ScrollTrigger)

    const instance = new LenisClass({
      duration: 1.2,
      easing: t => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    })

    // Sync Lenis' internal target with where the page actually is. This used
    // to force the top, which was right when Lenis started synchronously with
    // the page; now that it starts a moment later, a visitor who has already
    // scrolled would be yanked back up. `scrollBehavior` (app/router.options.ts)
    // has already positioned the page on navigation.
    instance.scrollTo(window.scrollY, { immediate: true })

    const onTick = (time: number) => instance.raf(time * 1000)
    // When ScrollTrigger refreshes (e.g. after a GSAP pin spacer is inserted),
    // tell Lenis to re-measure the page height so its scroll limit stays correct.
    const onRefresh = () => instance.resize()

    instance.on('scroll', ScrollTrigger.update)
    gsap.ticker.add(onTick)
    gsap.ticker.lagSmoothing(0)
    ScrollTrigger.addEventListener('refresh', onRefresh)

    lenis = instance
    teardown = () => {
      gsap.ticker.remove(onTick)
      ScrollTrigger.removeEventListener('refresh', onRefresh)
      instance.destroy()
    }
  }

  function disable() {
    wanted = false
    teardown?.()
    teardown = null
    lenis = null
  }

  watch(
    () => route.meta.smoothScroll === true,
    wants => (wants ? enable() : disable()),
    { immediate: true },
  )

  // Handle scroll position on every route change.
  // - No hash → reset to top (Lenis or native).
  // - Hash present → scroll to the target element via Lenis.
  //   For cross-page navigation the element may not be in the DOM yet at
  //   afterEach time, so we try immediately then retry after the page renders.
  const router = useRouter()
  router.afterEach((to) => {
    if (to.hash) {
      const scrollToHash = (retry = false) => {
        const el = document.querySelector(to.hash)
        if (el) {
          if (lenis) lenis.scrollTo(el as HTMLElement, { offset: -80 })
          else el.scrollIntoView({ behavior: 'smooth' })
        }
        else if (!retry) {
          // Element not in DOM yet (cross-page nav): wait for page to render
          setTimeout(() => scrollToHash(true), 350)
        }
      }
      nextTick(() => scrollToHash())
    }
    else {
      if (lenis) {
        lenis.scrollTo(0, { duration: 0.8 })
      }
      else {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }
  })

  return {
    provide: {
      // Stable façade so `$lenis?.resize()` call sites work whether or not
      // Lenis is currently active.
      lenis: {
        resize: () => lenis?.resize(),
        scrollTo: (target: Parameters<Lenis['scrollTo']>[0], opts?: Parameters<Lenis['scrollTo']>[1]) => lenis?.scrollTo(target, opts),
        start: () => lenis?.start(),
        stop: () => lenis?.stop(),
        get raw() {
          return lenis
        },
      },
    },
  }
})
